/**
 * Home — fase "durante": a tela principal do produto.
 *
 * Home v2 (PO, 19/09/2026 — protótipo B escolhido, com a hierarquia de quem
 * está viajando). Princípio: a barra é navegação; a Home é ESTADO. Cada
 * bloco só aparece quando existe, e a tela é relativa a quem usa — em grupo
 * (2+ pessoas) há blocos que sozinho não fazem sentido.
 *
 *   0. faixa de alerta, fixa no topo — aviso do grupo, ponto de encontro,
 *      enquete aberta sem o meu voto, próxima atividade em ≤ 15 min;
 *   1. roteiro — o hero laranja com a atividade de agora / a seguir e os
 *      chips do dia (feito · agora · a seguir); sem programação, "dia livre";
 *   2. financeiro — você · grupo · saldo (só você, sozinho);
 *   3. fotos recentes — uma linha, e "+ foto"; "ver mais" abre o resto;
 *   4. contextuais — o carro, enquanto estiver estacionado; o ranking em uma
 *      linha (só em grupo, com a feature ligada);
 *   5. hospedagem, quando cadastrada;
 *   6. o grupo — avatares, quantos são, e o que há para dizer (só em grupo);
 *   7. o próximo aniversário (só em grupo).
 *
 * O que o protótipo mostrava e ainda não tem fonte real (clima, notícias,
 * sugestões por lugar) não é exibido — está na Visão §10.3c, para depois.
 */
import { featureEnabled, formatMoney, pluralize, type CalendarDate } from '@nv/core';
import { useNavigate } from 'react-router-dom';

import { useTrip } from '../../../trip/TripContext';
import { Icon } from '../../../ui/icons';
import { Avatar } from '../../../ui/primitives';
import { AniversariosNaHome } from '../../aniversarios/AniversariosNaHome';
import { CarroNaHome } from '../../carro/CarroNaHome';
import { EncontroNaHome } from '../../encontro/EncontroNaHome';
import { useEnquetes } from '../../enquetes/api';
import { ConviteDeFamiliaNaHome } from '../../grupo/ConviteDeFamiliaNaHome';
import { PedidosDeSugestaoNaHome } from '../../sugestoes/PedidosNaHome';
import { HospedagemNaHome } from '../../hospedagem/HospedagemNaHome';
import { FotosRecentesNaHome } from '../../memorias/FotosRecentesNaHome';
import { RankingNaHome } from '../../ranking/RankingNaHome';
import type { HomeData } from '../data';
import type { TripHeader } from '../model';

export function DuringPhase({
  header,
  data,
  currency,
  hoje,
}: {
  header: TripHeader;
  data: HomeData;
  currency: string;
  /** Data civil de hoje na timezone da viagem — o alvo do card de hoje. */
  hoje: CalendarDate | null;
}) {
  const navigate = useNavigate();
  const { trip, tripId, members } = useTrip();
  const agora = data.now;
  const dia = hoje ?? '';
  const faltam = data.minutosAteProxima;
  // O hero já diz "começa em N min"; a faixa só repete quando é urgente de
  // verdade — 15 min é "sai agora" (o alerta por push usa o mesmo prazo).
  const alertaProxima = agora && faltam !== null && faltam <= 15;
  const emGrupo = members.length >= 2;

  // Enquete aberta em que eu ainda não votei — entra na faixa, abaixo do aviso
  // e do encontro (é decisão do grupo, mas espera; o aviso não).
  const enquetesLigadas = featureEnabled(trip.features, 'enquetes');
  const enquetes = useEnquetes(tripId, undefined, enquetesLigadas && emGrupo);
  const pendentes = (enquetes.data?.polls ?? []).filter((e) => e.isOpen && e.myOptionId === null);
  const enquete = pendentes[0];

  const estadoDe = (a: { id: string; startTime: string; status: string }): 'feita' | 'agora' | 'depois' => {
    if (a.status === 'concluida') return 'feita';
    if (agora && a.id === agora.id) return 'agora';
    // Antes da de "agora" na ordem do dia = já passou; depois = ainda vem.
    if (agora && a.startTime < agora.time) return 'feita';
    return agora ? 'depois' : 'feita';
  };

  return (
    <>
      {/* 0. Faixa de alerta — fixa no topo enquanto houver algo. */}
      <div className="home-faixa" data-testid="home-faixa">
        <EncontroNaHome />
        <ConviteDeFamiliaNaHome />
        <PedidosDeSugestaoNaHome />
        {enquete && (
          <button type="button" className="home-alerta home-alerta--enquete" onClick={() => navigate('/enquetes')} data-testid="home-alerta-enquete">
            <span className="home-alerta__icone" aria-hidden="true"><Icon name="enquete" size={18} /></span>
            <span className="home-alerta__texto">
              <b>Enquete aberta:</b> {enquete.question}
              {pendentes.length > 1 ? ` (+${pendentes.length - 1})` : ''}
            </span>
            <Icon name="avancar" size={16} />
          </button>
        )}
        {alertaProxima && (
          <button type="button" className="home-alerta" onClick={() => navigate(`/roteiro/${dia}/${agora.id}`)} data-testid="home-alerta-proxima">
            <span className="home-alerta__icone" aria-hidden="true"><Icon name="horario" size={18} /></span>
            <span className="home-alerta__texto">
              <b>{faltam === 0 ? 'Agora' : `Em ${pluralize(faltam, 'min', 'min')}`}:</b> {agora.title}
              {agora.location ? ` · ${agora.location}` : ''}
            </span>
            <Icon name="avancar" size={16} />
          </button>
        )}
      </div>

      {/* 1. Roteiro — o hero de agora + os chips do dia. */}
      <section className="now-card" aria-labelledby="now-title" data-testid="home-hoje">
        <div className="now-card__label">
          {agora?.emCurso && <span className="now-card__ponto" aria-hidden="true" />}
          Dia {header.dayNumber ?? '–'} de {header.durationInDays}
          {data.progresso && (
            <span data-testid="home-progresso"> · {data.progresso.concluidas} de {pluralize(data.progresso.total, 'atividade feita', 'atividades feitas')}</span>
          )}
        </div>
        {agora ? (
          <>
            {agora.coverThumbUrl && <img className="now-card__capa" src={agora.coverThumbUrl} alt="" data-testid="now-capa" />}
            <div className="now-card__time">{agora.time}</div>
            <h2 className="now-card__title" id="now-title">{agora.title}</h2>
            <p className="now-card__place">
              {agora.emCurso ? 'Acontecendo agora' : faltam !== null ? `Começa em ${pluralize(faltam, 'min', 'min')}` : 'A seguir'}
              {agora.location ? ` · ${agora.location}` : ''}
            </p>
            <div className="now-card__actions">
              <button type="button" className="nv-btn nv-btn--on-accent" onClick={() => navigate(`/roteiro/${dia}/${agora.id}`)}>
                Detalhes
              </button>
              {agora.location && (
                <button type="button" className="nv-btn nv-btn--on-accent nv-btn--vazado" onClick={() => navigate('/mapa')}>
                  Onde é
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h2 className="now-card__title" id="now-title">
              {data.atividadesDeHoje.length > 0 ? 'Por hoje é só' : 'Hoje ainda não tem programação'}
            </h2>
            <p className="now-card__place">
              {data.atividadesDeHoje.length > 0 ? 'Tudo o que estava marcado já aconteceu.' : 'Adicione a primeira atividade do dia — o grupo inteiro vê na hora.'}
            </p>
            <div className="now-card__actions">
              <button type="button" className="nv-btn nv-btn--on-accent" onClick={() => navigate(dia === '' ? '/roteiro' : `/roteiro/${dia}/nova`)}>
                Nova atividade
              </button>
              {data.atividadesDeHoje.length > 0 && (
                <button type="button" className="nv-btn nv-btn--on-accent nv-btn--vazado" onClick={() => navigate(`/roteiro/${dia}`)}>
                  Ver o dia
                </button>
              )}
            </div>
          </>
        )}
      </section>
      {data.atividadesDeHoje.length > 0 && (
        <div className="dia-chips" data-testid="home-chips">
          {data.atividadesDeHoje.map((a) => {
            const estado = estadoDe(a);
            return (
              <button type="button" key={a.id} className={`dia-chip dia-chip--${estado}`} data-estado={estado} onClick={() => navigate(`/roteiro/${dia}/${a.id}`)}>
                <span className="dia-chip__hora">{estado === 'feita' ? <Icon name="concluir" size={11} /> : null}{a.startTime}</span>
                <span className="dia-chip__titulo">{a.title}</span>
              </button>
            );
          })}
          <button type="button" className="dia-chip dia-chip--ver" onClick={() => navigate(`/roteiro/${dia}`)} disabled={dia === ''}>
            <span className="dia-chip__hora">o dia</span>
            <span className="dia-chip__titulo">Ver tudo <Icon name="avancar" size={12} /></span>
          </button>
        </div>
      )}

      {/* 2. Financeiro — números, não atalho: você · grupo · saldo. */}
      <button type="button" className="home-metrics fin-home" onClick={() => navigate('/financeiro')} data-testid="home-financeiro">
        <span className="fin-home__bloco">
          <span className="fin-home__rotulo">Você gastou</span>
          <span className="fin-home__valor">{data.finance ? formatMoney(data.finance.myTotalMinor, currency, { hideCents: true }) : '—'}</span>
        </span>
        {emGrupo && (
          <span className="fin-home__bloco">
            <span className="fin-home__rotulo">O grupo</span>
            <span className="fin-home__valor">{data.finance ? formatMoney(data.finance.groupTotalMinor, currency, { hideCents: true }) : '—'}</span>
          </span>
        )}
        {emGrupo && data.finance && (
          <span className="fin-home__bloco">
            <span className="fin-home__rotulo">{data.finance.balanceMinor >= 0 ? 'A receber' : 'A pagar'}</span>
            <span className={data.finance.balanceMinor >= 0 ? 'fin-home__valor fin-home__valor--receber' : 'fin-home__valor fin-home__valor--pagar'}>
              {formatMoney(Math.abs(data.finance.balanceMinor), currency, { hideCents: true })}
            </span>
          </span>
        )}
        {!emGrupo && (
          <span className="fin-home__bloco fin-home__bloco--ir">
            <span className="fin-home__rotulo">Financeiro</span>
            <span className="fin-home__ir">Ver gastos <Icon name="avancar" size={14} /></span>
          </span>
        )}
      </button>

      {/* 3. Fotos recentes — só quando há foto (e o "+ foto" sempre que dá). */}
      <FotosRecentesNaHome />

      {/* 4. Contextuais — cada um pela sua regra. */}
      <CarroNaHome />
      {emGrupo && <RankingNaHome currency={currency} />}

      {/* 5. Onde estamos — só quando há hospedagem cadastrada. */}
      <HospedagemNaHome />

      {/* 6. O grupo — só em grupo. */}
      {emGrupo && (
        <button type="button" className="nv-card nv-card--surf grupo-home" onClick={() => navigate('/grupo')} data-testid="home-grupo">
          <span className="nv-avatar-stack">
            {members.slice(0, 5).map((m) => (
              <Avatar key={m.memberId} person={{ id: m.memberId, name: m.name, initials: m.name.trim().slice(0, 1).toUpperCase(), color: m.familyColor ?? 'var(--chip)', url: m.avatarUrl ?? null }} size={30} />
            ))}
          </span>
          <span className="grupo-home__texto">
            <span className="grupo-home__titulo">{pluralize(members.length, 'pessoa na viagem', 'pessoas na viagem')}</span>
            <span className="grupo-home__detalhe">
              {(() => {
                const familias = new Set(members.map((m) => m.familyId).filter(Boolean)).size;
                return familias > 1 ? `${familias} famílias · ver quem vai` : 'Ver quem vai, convites e famílias';
              })()}
            </span>
          </span>
          <Icon name="avancar" size={16} />
        </button>
      )}

      {/* 7. O próximo aniversário do grupo — só em grupo. */}
      {emGrupo && <AniversariosNaHome hoje={hoje} />}
    </>
  );
}
